import { FC, useCallback, useEffect, useState } from 'react'
import { PhoneSpecs } from '../../phones'
import { Box } from '../index'
import { PhoneCard } from './phone-card'
import InfiniteScroll from 'react-infinite-scroll-component'
import { Flex, Text } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'

export interface PhoneListProps {
  phones: PhoneSpecs[]
  onClick: (modelId: PhoneSpecs['modelId'] | string) => void
}

export const LAZY_LOAD_ITEMS_SIZE = 12

const calculateCursorIndex = ({ phones, startingAt = 0 }: { phones: PhoneSpecs[]; startingAt?: number }) =>
  Math.min(startingAt + LAZY_LOAD_ITEMS_SIZE, phones.length)

export const PhoneList: FC<PhoneListProps> = ({ phones, onClick }) => {
  const { t } = useTranslation()
  const [cursorIndex, setCursorIndex] = useState(calculateCursorIndex({ phones }))

  const displayedPhones = phones
    .map((phone, index) => <PhoneCard key={`${phone.modelSlug}-${index}`} phone={phone} onClick={onClick} />)
    .splice(0, cursorIndex)

  const noPhonesMessage = useCallback(() => {
    if (displayedPhones.length) {
      return null
    }

    return (
      <Box layerStyle={'error'}>
        <Text>{t('error_no-devices-found')}</Text>
        <Text>{t('error_please-go-back') as string}</Text>
      </Box>
    )
  }, [t, displayedPhones])

  useEffect(() => {
    setCursorIndex(calculateCursorIndex({ phones }))
  }, [phones])

  return (
    <Box marginTop={4}>
      {noPhonesMessage()}

      <InfiniteScroll
        dataLength={displayedPhones.length}
        next={() => {
          setCursorIndex(calculateCursorIndex({ phones, startingAt: cursorIndex }))
        }}
        hasMore={cursorIndex < phones.length && phones.length > LAZY_LOAD_ITEMS_SIZE}
        hasChildren={displayedPhones.length > 0}
        loader={<h4>Loading...</h4>}
      >
        <Flex marginTop={4} flexWrap="wrap" justifyContent="center">
          {displayedPhones}
        </Flex>
      </InfiniteScroll>
    </Box>
  )
}
